import React from 'react';
import { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  Divider,
} from '@mui/material';
import { saveAs } from 'file-saver';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Adjust these to match the actual values your backend expects for "operation"
const OPERATION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'New', label: 'New Enquiry' },
  { value: 'FollowUp', label: 'Follow Up' },
  { value: 'Converted', label: 'Converted' },
  { value: 'Closed', label: 'Closed' },
];

function EnquiryReport({ branchId }) {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedOption, setSelectedOption] = React.useState('all');
  const [selectedClass, setSelectedClass] = React.useState('');
  const [selectedOperation, setSelectedOperation] = React.useState('all');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [ddlClass, setDdlClass] = useState([]);
  const [schoolData, setSchoolData] = useState([]);
  const [reportData, setReportData] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem('token');
  

  const fetchSchoolDetails = async () => {
    try {
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching school details: ${response.status}`);
      }
      const data = await response.json();
      setSchoolData(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDdlClass = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching classes: ${response.status}`);
      }
      const data = await response.json();
      setDdlClass(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOptionChange = (event) => setSelectedOption(event.target.value);
  const handleClassChange = (event) => setSelectedClass(event.target.value);
  const handleOperationChange = (event) => setSelectedOperation(event.target.value);
  const handleFromDateChange = (event) => setFromDate(event.target.value);
  const handleToDateChange = (event) => setToDate(event.target.value);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const getLogoDataUrl = async (schoolRecord) => {
    const fallbackLogo =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';

    try {
      const rawLogo =
        schoolRecord?.logoImg ||
        schoolRecord?.headerLogoImg ||
        schoolRecord?.logo ||
        '';

      if (!rawLogo || typeof rawLogo !== 'string') {
        return fallbackLogo;
      }

      if (rawLogo.startsWith('http') || rawLogo.startsWith('data:image')) {
        return rawLogo;
      }

      let cleanedBase64 = rawLogo
        .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
        .replace(/\s/g, '')
        .replace(/\n/g, '');
      const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
      cleanedBase64 += '='.repeat(paddingLength);

      const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
      if (!isValidBase64 || cleanedBase64.length === 0) {
        return fallbackLogo;
      }

      const dataUrl = `data:image/png;base64,${cleanedBase64}`;

      const isValidImage = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = dataUrl;
      });

      return isValidImage ? dataUrl : fallbackLogo;
    } catch (error) {
      console.error('Error preparing logo image:', error);
      return fallbackLogo;
    }
  };

const handleSave = async () => {
    try {
      if (!branchId) {
  setSnackbarMessage('Please select a branch!');
  setOpenSnackbar(true);
  return;
}
      const requestUrl = `${apiUrl}/FeeReport/GetEnquiryReport?Branch_id=${branchId}`;
      const requestBody = {
        operation: selectedOperation,
        classId: selectedOption === 'class' && selectedClass ? selectedClass : 0,
        fromDate: fromDate,
        toDate: toDate,
      };

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      // Backend returns 200 OK even for logical errors (e.g. bad date),
      // so check the status field inside the body, not just response.ok
      if (!response.ok || result.status === 'Error') {
        setSnackbarMessage(result.error || result.msg || 'Please provide proper input');
        setOpenSnackbar(true);
        return;
      }

      if (result.msg === 'Record Not Found' || !Array.isArray(result.data) || result.data.length === 0) {
        setSnackbarMessage('No records found!');
        setOpenSnackbar(true);
        setReportData([]);
        return;
      }

      const data = result.data;
      setReportData(data);

      const schoolRecord = schoolData && schoolData[0] ? schoolData[0] : {};
      const schoolName = schoolRecord.schoolName || 'School Name';
      const logoDataUrl = await getLogoDataUrl(schoolRecord);
      const className =
        selectedOption === 'class' && selectedClass
          ? (ddlClass.find((c) => c.classId === selectedClass)?.className || 'All')
          : 'All Students';
      const reportTypeLabel = selectedOption === 'all' ? 'All Students' : `Class-wise (${className})`;
      const operationLabel =
        OPERATION_OPTIONS.find((o) => o.value === selectedOperation)?.label || 'All';
      const generatedOn = new Date().toLocaleString();

      const reportWindow = window.open('', '_blank');

      if (!reportWindow) {
        setSnackbarMessage('Please allow pop-ups to view the report!');
        setOpenSnackbar(true);
        return;
      }

      reportWindow.document.write(`
        <html>
          <head>
            <title>Enquiry Report</title>
            <style>

              * {
                box-sizing: border-box;
              }

              body {
                font-family: 'Segoe UI', Arial, sans-serif;
                background-color: #eef1f7;
                margin: 0;
                padding: 24px;
                color: #212121;
              }

              .report-container {
                background-color: #ffffff;
                max-width: 1100px;
                margin: 0 auto;
                border-radius: 10px;
                box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
                overflow: hidden;
              }

              .navbar {
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 18px 24px;
                background: linear-gradient(90deg, #00796b 0%, #26a69a 100%);
                border-bottom: 4px solid #004d40;
              }

              .logo-wrapper {
                width: 56px;
                height: 56px;
                background-color: #ffffff;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
              }

              .logo {
                max-width: 46px;
                max-height: 46px;
                object-fit: contain;
              }

              .header-text {
                flex-grow: 1;
                text-align: center;
              }

              .school-name {
                font-size: 22px;
                font-weight: 700;
                color: #ffffff;
                margin: 0;
                letter-spacing: 0.3px;
              }

              .report-title {
                font-size: 14px;
                font-weight: 500;
                color: #e0f2f1;
                margin: 2px 0 0 0;
                text-transform: uppercase;
                letter-spacing: 1.5px;
              }

              .meta-bar {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                justify-content: space-between;
                align-items: center;
                padding: 14px 24px;
                background-color: #f5f6fb;
                border-bottom: 1px solid #e0e0e0;
                font-size: 13px;
              }

              .meta-item {
                display: flex;
                flex-direction: column;
              }

              .meta-label {
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #757575;
                margin-bottom: 2px;
              }

              .meta-value {
                font-size: 13px;
                font-weight: 600;
                color: #00796b;
              }

              .table-wrapper {
                padding: 20px 24px 8px 24px;
                overflow-x: auto;
              }

              table {
                border-collapse: collapse;
                width: 100%;
              }

              th, td {
                border: 1px solid #e0e0e0;
                text-align: left;
                padding: 10px 12px;
                font-size: 12.5px;
              }

              th {
                background-color: #00695c;
                color: #ffffff;
                font-size: 13px;
                font-weight: 600;
                position: sticky;
                top: 0;
              }

              tbody tr:nth-child(even) {
                background-color: #e0f2f1;
              }

              tbody tr:hover {
                background-color: #b2dfdb;
              }

              .footer-bar {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 24px 24px 24px;
                font-size: 12px;
                color: #757575;
              }

              .print-icon {
                margin-right: 6px;
              }

              .print-button {
                background-color: #4CAF50;
                border: none;
                color: white;
                padding: 10px 22px;
                text-align: center;
                text-decoration: none;
                font-size: 14px;
                font-weight: 600;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                align-items: center;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
                transition: background-color 0.2s ease;
              }

              .print-button:hover {
                background-color: #43a047;
              }

              .print-button-container {
                display: flex;
                justify-content: flex-end;
                padding: 0 24px 24px 24px;
              }

              .no-data {
                padding: 24px;
                text-align: center;
                color: #757575;
                font-size: 14px;
              }

              @media print {
                body {
                  background-color: #ffffff;
                  padding: 0;
                }
                .report-container {
                  box-shadow: none;
                  border-radius: 0;
                  max-width: 100%;
                }
                .print-button-container {
                  display: none;
                }
                th {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                tbody tr:nth-child(even) {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                .navbar {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }

            </style>
          </head>
          <body>
            <div class="report-container">
              <div class="navbar">
                <div class="logo-wrapper">
                  <img class="logo" src="${logoDataUrl}" alt="Logo" />
                </div>
                <div class="header-text">
                  <p class="school-name">${schoolName}</p>
                  <p class="report-title">Enquiry Report</p>
                </div>
              </div>

              <div class="meta-bar">
                <div class="meta-item">
                  <span class="meta-label">Report Type</span>
                  <span class="meta-value">${reportTypeLabel}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Operation</span>
                  <span class="meta-value">${operationLabel}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Date Range</span>
                  <span class="meta-value">${fromDate ? formatDate(fromDate) : ''} to ${toDate ? formatDate(toDate) : ''}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Total Records</span>
                  <span class="meta-value">${Array.isArray(data) ? data.length : 0}</span>
                </div>
              </div>

              <div class="table-wrapper">
            ${
                  Array.isArray(data) && data.length > 0
                    ? `
                <table>
                  <thead>
                    <tr>
                      <th>Enquiry Code</th>
                      <th>Student Name</th>
                      <th>Parent / Guardian</th>
                      <th>Mother Name</th>
                      <th>Contact No</th>
                      <th>Email</th>
                      <th>Class</th>
                      <th>Enquiry Date</th>
                      <th>Followup Date</th>
                      <th>Enquiry Status</th>
                      <th>Conversion Status</th>
                      <th>Registration No</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${data.map(item => `
                      <tr>
                        <td>${item.EnquiryCode ?? '-'}</td>
                        <td>${item.StudentName ?? '-'}</td>
                        <td>${item.ParentAndGurdian ?? '-'}</td>
                        <td>${item.MotherName ?? '-'}</td>
                        <td>${item.ContactNo ?? '-'}</td>
                        <td>${item.EmailId ?? '-'}</td>
                        <td>${item.ClassName ?? '-'}</td>
                        <td>${item.EnquiryDate ?? '-'}</td>
                        <td>${item.FollowupDate ?? '-'}</td>
                        <td>${item.EnquiryStatus ?? '-'}</td>
                        <td>${item.RegistrationConversionStatus ?? '-'}</td>
                        <td>${item.RegistrationNo ?? '-'}</td>
                        <td>${item.Remark ?? '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
                `
                    : `<div class="no-data">No records to display</div>`
                }
              </div>

              <div class="footer-bar">
                <span>Generated on: ${generatedOn}</span>
                <span>&copy; ${new Date().getFullYear()} ${schoolName}</span>
              </div>
            </div>

            <div class="print-button-container">
              <button class="print-button" onclick="window.print()">
                <span class="print-icon">&#128438;</span> Print
              </button>
            </div>
          </body>
        </html>
      `);

      reportWindow.document.close();
      reportWindow.focus();
    } catch (error) {
      console.error('Error fetching report data:', error);
      setSnackbarMessage('Something went wrong while generating the report!');
      setOpenSnackbar(true);
    }
  };

  const generateCSV = () => {
    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      console.error('No report data available');
      return;
    }

    let csvContent = '';
    const keys = Object.keys(reportData[0]);
    csvContent += keys.map(key => key.toUpperCase()).join(',') + '\n';
    reportData.forEach(item => {
      const values = keys.map(key => item[key]);
      csvContent += values.join(',') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'enquiry_report.csv');
  };

  const buttonStyles = {
    generate: {
      background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
      borderRadius: "25px",
      padding: "10px 25px",
      width: "150px",
      boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)",
        transform: "translateY(-2px)",
        boxShadow: "0 5px 10px rgba(33, 203, 243, .4)",
      },
    },
    csv: {
      background: "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
      borderRadius: "25px",
      padding: "10px 25px",
      width: "150px",
      boxShadow: "0 3px 5px 2px rgba(244, 67, 54, .3)",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(45deg, #d32f2f 30%, #e53935 90%)",
        transform: "translateY(-2px)",
        boxShadow: "0 5px 10px rgba(244, 67, 54, .4)",
      },
    },
  };

  const selectStyles = {
    width: "250px",
    background: "linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)",
    borderRadius: "15px",
    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2196F3",
      borderWidth: "2px",
    },
    "&:hover": {
      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
      transform: "translateY(-2px)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1976D2",
      },
    },
    "&.Mui-focused": {
      boxShadow: "0 6px 12px rgba(33, 150, 243, 0.3)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#21CBF3",
      },
    },
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontWeight: 500,
      color: "#333",
    },
  };

  const getFormattedDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);

    setToDate(getFormattedDate(today));
    setFromDate(getFormattedDate(oneMonthAgo));
    fetchDdlClass();
    fetchSchoolDetails();
  }, []);

  return (
    <Grid container justifyContent="center" style={{ margin: '0 auto', maxWidth: '700px' }}>
      <Grid item xs={12}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: '14px',
            padding: { xs: '20px', sm: '28px' },
            background: 'linear-gradient(145deg, #ffffff 0%, #f7f9fc 100%)',
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: '#00796b', textAlign: 'center', mb: 0.5 }}
          >
            Enquiry Report
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#757575', textAlign: 'center', mb: 2 }}
          >
            Generate and export student enquiry records
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <RadioGroup row aria-label="student" name="student" value={selectedOption} onChange={handleOptionChange}>
                  <FormControlLabel value="all" control={<Radio />} label="All Students" />
                  <FormControlLabel value="class" control={<Radio />} label="Class-wise Students" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {selectedOption === 'class' && (
                <FormControl fullWidth>
                  <Select value={selectedClass} onChange={handleClassChange} sx={selectStyles} displayEmpty>
                    <MenuItem value="" disabled>Select Class</MenuItem>
                    {ddlClass.map((classItem) => (
                      <MenuItem key={classItem.classId} value={classItem.classId}>
                        {classItem.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Select value={selectedOperation} onChange={handleOperationChange} sx={selectStyles}>
                  {OPERATION_OPTIONS.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ ...selectStyles, width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ ...selectStyles, width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} container spacing={1} justifyContent="center" sx={{ marginBottom: 1, marginTop: 1 }}>
              <Grid item xs={6} sm={4}>
                <Button
                disabled={!branchId}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  fullWidth
                  size="small"
                  sx={{ ...buttonStyles.generate, minWidth: '150px' }}
                >
                  Generate Report
                </Button>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={generateCSV}
                  fullWidth
                  size="small"
                  sx={{ ...buttonStyles.csv, minWidth: '150px' }}
                >
                  Download CSV
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default EnquiryReport;